import type { Container } from 'pixi.js';
import { WinStreakController } from './winStreak/WinStreakController';
import { SeasonPassController } from './seasonPass/SeasonPassController';
import { RaceEventController } from './raceEvent/RaceEventController';
import { LuckyWheelController } from './luckyWheel/LuckyWheelController';
import { PiggyBankController } from './piggyBank/PiggyBankController';
import { createWinStreakDependencies } from './adapters/createWinStreakDependencies';
import type { App } from '../app/App';
import { createSeasonPassDependencies } from './adapters/createSeasonPassDependencies';
import { createRaceEventDependencies } from './adapters/createRaceEventDependencies';
import type { MenuIcon } from '../mainMenu/types';
import type { GameEventController } from './types';
import { isSeasonPassTurnOn } from './seasonPass/isSeasonPassTurnOn';
import { createLuckyWheelDependencies } from './adapters/createLuckyWheelDependencies';
import { createPiggyBankDependencies } from './adapters/createPiggyBankDependencies';
import { createCollectionEventDependencies } from './adapters/createCollectionEventDependencies';
import { CollectionEventController } from './collectionEvent/CollectionEventController';
import type { BoardModel } from '../game/boardMVC/models/board/BoardModel';
import type { BoardView } from '../game/boardMVC/views/board/BoardView';
import { VersusController } from '../gameJam/VersusController';
import { hs854isPVPAvailable } from '../app/splitTest/tests/Hs854pvp';

export class GameEventsManager {
  private winStreak: WinStreakController | null = null;

  private seasonPass: SeasonPassController | null = null;

  private raceEvent: RaceEventController | null = null;

  private luckyWheel: LuckyWheelController | null = null;

  private piggyBank: PiggyBankController | null = null;

  public collectionEvent: CollectionEventController | null = null;

  public versus: VersusController | null = null;

  private readonly controllers: GameEventController[] = [];

  constructor(private readonly app: App) {}

  public init(): void {
    this.luckyWheel = new LuckyWheelController(createLuckyWheelDependencies(this.app));
    this.controllers.push(this.luckyWheel);

    this.piggyBank = new PiggyBankController(createPiggyBankDependencies(this.app));
    this.controllers.push(this.piggyBank);

    this.collectionEvent = new CollectionEventController(createCollectionEventDependencies(this.app));
    this.controllers.push(this.collectionEvent);

    this.winStreak = new WinStreakController(createWinStreakDependencies(this.app));
    this.controllers.push(this.winStreak);

    this.raceEvent = new RaceEventController(createRaceEventDependencies(this.app));
    this.controllers.push(this.raceEvent);

    if (isSeasonPassTurnOn()) {
      this.seasonPass = new SeasonPassController(createSeasonPassDependencies(this.app));
      this.controllers.push(this.seasonPass);
    } else {
      this.seasonPass = null;
    }

    if (hs854isPVPAvailable()) {
      this.versus = new VersusController(this.app);
      this.controllers.push(this.versus);
    }
  }

  public createMenuIcons(): MenuIcon[] {
    const icons: { icon: MenuIcon; unlockLevel: number }[] = [];
    this.controllers.forEach((controller) => {
      if (controller.createGameEventIcon) {
        icons.push(controller.createGameEventIcon());
      }
    });

    return icons.sort((a, b) => a.unlockLevel - b.unlockLevel).map(({ icon }) => icon);
  }

  public setRewardTargets(coinTarget: Container | undefined, boosterTarget: Container | undefined): void {
    if (!coinTarget || !boosterTarget) return;
    this.controllers.forEach((controller) => {
      controller?.setRewardTargets?.(coinTarget, boosterTarget);
    });
  }

  public setPlayLevelAction(action: () => Promise<void>): void {
    this.controllers.forEach((controller) => {
      controller?.setPlayLevelAction?.(action);
    });
  }

  public setToggleCoinCounterUpdate(action: (value: boolean, checkForUpdate?: boolean) => void): void {
    this.controllers.forEach((controller) => {
      controller?.toggleCoinCounterUpdate?.(action);
    });
  }

  public async startMainMenuFlow(lifeCycleObject: Container): Promise<void> {
    for (let i = 0; i < this.controllers.length; i += 1) {
      if (lifeCycleObject.destroyed) return;
      const controller = this.controllers[i];
      if (controller.startMainMenuFlow) {
        await controller.startMainMenuFlow();
      }
    }
  }

  public onLevelWon(): void {
    this.controllers.forEach((controller) => {
      controller?.onLevelWon?.();
    });
  }

  public onLevelLost(): void {
    this.controllers.forEach((controller) => {
      controller?.onLevelLost?.();
    });
  }

  public createLostProgressBadges(): Container[] {
    const badges: Container[] = [];
    this.controllers.forEach((controller) => {
      const badge = controller?.createLostProgressBadge?.() ?? null;
      if (badge) badges.push(badge);
    });

    return badges;
  }

  public getDebugFunctions(): Record<string, Record<string, () => void>> {
    const out: Record<string, Record<string, () => void>> = {};
    this.controllers.forEach((controller) => {
      const debugFunctions = controller?.getDebugFunctions?.() ?? null;
      if (debugFunctions) {
        out[controller.featureName] = debugFunctions;
      }
    });
    return out;
  }

  public setupInGameHandlers(boardModel: BoardModel, boardView: BoardView): void {
    this.controllers.forEach((controller) => {
      controller?.setupInGameHandler?.(boardModel, boardView);
    });
  }
}
